import { createRoot } from "react-dom/client";
import { MrWineWidget } from "./MrWineWidget";
import { widgetStyles } from "./styles";
import type { WidgetConfig, WidgetPosition, WidgetTheme } from "./types";

const VALID_POSITIONS: WidgetPosition[] = [
  "bottom-right",
  "bottom-left",
  "top-right",
  "top-left",
];
const VALID_THEMES: WidgetTheme[] = ["dark", "light"];

const DEFAULTS = {
  theme: "dark" as WidgetTheme,
  accentColor: "#00d4aa",
  position: "bottom-right" as WidgetPosition,
  lang: "en",
};

function getCurrentScript(): HTMLScriptElement | null {
  if (document.currentScript instanceof HTMLScriptElement) {
    return document.currentScript;
  }
  // Fallback: find any script tag whose src ends with widget.js
  const scripts = Array.from(document.getElementsByTagName("script"));
  return (
    scripts.reverse().find((s) => /widget(\.[a-z0-9]+)?\.js(\?|$)/i.test(s.src)) ?? null
  );
}

function defaultApiUrl(script: HTMLScriptElement | null): string {
  try {
    if (script?.src) {
      const url = new URL(script.src, window.location.href);
      return `${url.origin}/api/v1/chat`;
    }
  } catch {
    /* ignore */
  }
  return `${window.location.origin}/api/v1/chat`;
}

function readConfig(script: HTMLScriptElement | null): WidgetConfig {
  const data = script?.dataset ?? ({} as DOMStringMap);

  const themeRaw = (data.theme ?? DEFAULTS.theme) as WidgetTheme;
  const theme = VALID_THEMES.includes(themeRaw) ? themeRaw : DEFAULTS.theme;

  const positionRaw = (data.position ?? DEFAULTS.position) as WidgetPosition;
  const position = VALID_POSITIONS.includes(positionRaw)
    ? positionRaw
    : DEFAULTS.position;

  const accentColor = (data.color ?? DEFAULTS.accentColor).trim() || DEFAULTS.accentColor;
  const lang = (data.lang ?? DEFAULTS.lang).trim() || DEFAULTS.lang;
  const apiKey = (data.apiKey ?? "").trim();
  const apiUrl = (data.apiUrl ?? defaultApiUrl(script)).trim();

  return { apiKey, apiUrl, theme, accentColor, position, lang };
}

function injectGoogleFonts() {
  const FONTS_URL =
    "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap";
  if (document.querySelector(`link[href="${FONTS_URL}"]`)) return;
  const preconnect1 = document.createElement("link");
  preconnect1.rel = "preconnect";
  preconnect1.href = "https://fonts.googleapis.com";
  document.head.appendChild(preconnect1);
  const preconnect2 = document.createElement("link");
  preconnect2.rel = "preconnect";
  preconnect2.href = "https://fonts.gstatic.com";
  preconnect2.crossOrigin = "anonymous";
  document.head.appendChild(preconnect2);
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = FONTS_URL;
  document.head.appendChild(link);
}

function mount() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const w = window as unknown as { __MRWINE_WIDGET_MOUNTED__?: boolean };
  if (w.__MRWINE_WIDGET_MOUNTED__) return;
  w.__MRWINE_WIDGET_MOUNTED__ = true;

  injectGoogleFonts();

  const script = getCurrentScript();
  const config = readConfig(script);

  if (!config.apiKey) {
    // No key → demo mode: the widget chats anonymously with the public
    // Sommelier. Orgs that want their catalog-restricted Sales Agent must
    // supply data-api-key.
    // eslint-disable-next-line no-console
    console.info(
      "[MrWine] No data-api-key supplied; running in demo mode (public Sommelier chat).",
    );
  }

  const host = document.createElement("div");
  host.id = "mrwine-widget-host";
  host.style.all = "initial";
  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: "open" });
  const styleEl = document.createElement("style");
  styleEl.textContent = widgetStyles;
  shadow.appendChild(styleEl);

  const mountPoint = document.createElement("div");
  shadow.appendChild(mountPoint);

  createRoot(mountPoint).render(<MrWineWidget config={config} />);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mount, { once: true });
} else {
  mount();
}
